/**
 * OverviewView
 *
 * Top-level dashboard for Beu financials.
 *
 * Shows:
 *   - KPI counts (active salons / hairdressers / SE / MRR / ARR)
 *   - Month-to-date revenue summary vs prior month, with Stripe costs and net
 */

import React, { Component } from 'react';
import { adminService } from '../../../_services/admin.service';

const fmtGBP = (n, opts = {}) => {
    if (n == null || isNaN(n)) return '—';
    const { decimals = 2 } = opts;
    return '£' + Number(n).toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};
const fmtInt = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-GB');

class OverviewView extends Component {
    state = { loading: true, error: null, data: null };

    componentDidMount() {
        this.fetch();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
            this.fetch();
        }
    }

    fetch = async () => {
        this.setState({ loading: true, error: null });
        try {
            const { startDate, endDate } = this.props;
            const res = await adminService.getFinancialsOverview({ startDate, endDate });
            if (res.statusCode === 1) {
                this.setState({ data: res.responseData, loading: false });
            } else {
                this.setState({ error: res.message || 'Failed to load overview', loading: false });
            }
        } catch (err) {
            console.error('financials overview error', err);
            this.setState({ error: String(err), loading: false });
        }
    };

    renderKpiTiles(counts) {
        const tiles = [
            { label: 'Transacting salons', value: fmtInt(counts.activeSalons), sub: `${counts.registeredSalons ?? '—'} registered · ${counts.newSalonsThisMonth ?? 0} new this month` },
            { label: 'Active hairdressers', value: fmtInt(counts.activeHairdressers), sub: `Employed ${fmtInt(counts.activeEmployed)} · SE ${fmtInt(counts.activeSE)}` },
            { label: 'SE connect accts', value: fmtInt(counts.activeSEConnectAccounts), sub: 'Stripe-billed £1/mo each' },
            { label: 'MRR', value: fmtGBP(counts.mrr, { decimals: 0 }) },
            { label: 'ARR', value: fmtGBP(counts.arr, { decimals: 0 }) }
        ];
        return (
            <div className="fin-kpi-strip">
                {tiles.map(t => (
                    <div key={t.label} className="fin-kpi-tile">
                        <div className="fin-kpi-tile-label">{t.label}</div>
                        <div className="fin-kpi-tile-value">{t.value}</div>
                        {t.sub && <div className="fin-kpi-tile-sub">{t.sub}</div>}
                    </div>
                ))}
            </div>
        );
    }

    renderRevenueSummary(revenue) {
        const { mtd = {}, prev = {} } = revenue;
        const row = (label, mtdValue, prevValue, cls = '') => (
            <tr className={cls}>
                <td>{label}</td>
                <td>{fmtGBP(mtdValue)}</td>
                <td>{fmtGBP(prevValue)}</td>
            </tr>
        );
        return (
            <div className="fin-revenue-summary">
                <h3>Revenue summary</h3>
                <table className="fin-revenue-table">
                    <thead>
                        <tr>
                            <th>Stream</th>
                            <th>MTD</th>
                            <th>Prev month</th>
                        </tr>
                    </thead>
                    <tbody>
                        {row('Subscriptions', mtd.subscriptions, prev.subscriptions)}
                        {row('Payment processing', mtd.processing, prev.processing)}
                        {row('Instant withdrawals', mtd.instantWithdrawals, prev.instantWithdrawals)}
                        {row('Invoicing (SMS/email)', mtd.invoicing, prev.invoicing)}
                        {row('Gross revenue', mtd.gross, prev.gross, 'total')}
                        {row('Stripe costs', mtd.stripeCosts != null ? -Math.abs(mtd.stripeCosts) : null, prev.stripeCosts != null ? -Math.abs(prev.stripeCosts) : null, 'cost')}
                        {row('Net revenue', mtd.net, prev.net, 'net')}
                    </tbody>
                </table>
                {revenue.note && (
                    <p className="fin-dim" style={{ fontSize: 12, marginTop: 12 }}>{revenue.note}</p>
                )}
            </div>
        );
    }

    renderProcessingSnapshot(p) {
        if (!p) return null;
        return (
            <div className="fin-section">
                <div className="fin-section-header">
                    <h3 className="fin-section-title">Payment processing — MTD snapshot</h3>
                    <span className="fin-section-sub">
                        Stripe tier: <span className="fin-pill">{p.currentTier || '—'}</span>
                    </span>
                </div>
                <table className="fin-table">
                    <thead>
                        <tr>
                            <th>Channel</th>
                            <th className="num">Volume</th>
                            <th className="num">Txns</th>
                            <th className="num">Beu fee (gross)</th>
                            <th className="num">Stripe fee</th>
                            <th className="num">Net margin</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(p.byChannel || []).map(ch => (
                            <tr key={ch.channel}>
                                <td>{ch.channel}</td>
                                <td className="num">{fmtGBP(ch.volume)}</td>
                                <td className="num">{fmtInt(ch.txns)}</td>
                                <td className="num">{fmtGBP(ch.beuFee)}</td>
                                <td className="num">{fmtGBP(ch.stripeFee)}</td>
                                <td className="num">{fmtGBP(ch.beuFee - ch.stripeFee)}</td>
                            </tr>
                        ))}
                        <tr className="total">
                            <td>Total on-platform</td>
                            <td className="num">{fmtGBP(p.totalVolume)}</td>
                            <td className="num">{fmtInt(p.totalTxns)}</td>
                            <td className="num">{fmtGBP(p.totalBeuFee)}</td>
                            <td className="num">{fmtGBP(p.totalStripeFee)}</td>
                            <td className="num">{fmtGBP(p.totalBeuFee - p.totalStripeFee)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { loading, error, data } = this.state;
        if (loading) return <div className="fin-loading">Loading overview…</div>;
        if (error) return <div className="fin-error">{error}</div>;
        if (!data) return null;

        return (
            <div>
                {this.renderKpiTiles(data.counts || {})}
                {this.renderRevenueSummary(data.revenue || {})}
                {this.renderProcessingSnapshot(data.processing)}
            </div>
        );
    }
}

export default OverviewView;
